// Wingman AI Constants & Utilities

import { computeRelationshipStage } from "./utils/relationshipStage";
import { idbGet, idbSet, isIdbAvailable } from "./utils/idb";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Phase OC.1 — Outcome Capture V2 client feature flag. When OFF (default), all
// frontend outcome-related behavior is byte-identical to pre-OC.1. When ON:
//   * postOutcomeToBackend() forwards actual_user_response (RC-1)
//   * postOutcomeToBackend() prefers the last known real turn_index (RC-3)
//   * a fresh paste mints a new session_id (RC-4)
// Backend behavior is independently gated by OUTCOME_CAPTURE_V2 on the server.
export const OUTCOME_CAPTURE_V2_ENABLED =
  String(process.env.REACT_APP_OUTCOME_CAPTURE_V2 || "").trim().toLowerCase() === "true";

// Stale-Message Fix — flag retired 2026-07-15 (behavior is now permanent in
// App.js handleGenerate). Constant kept exported for 1-WEEK ROLLBACK SAFETY
// only; delete on or after 2026-07-22 along with REACT_APP_FIX_CONTINUATION_HISTORY
// entries in frontend/.env and Vercel env vars. Do NOT re-gate on this value.
export const FIX_CONTINUATION_HISTORY_ENABLED =
  String(process.env.REACT_APP_FIX_CONTINUATION_HISTORY || "").trim().toLowerCase() === "true";

// P0.4 — DESTINATION MODES (Conversation Direction Control Layer)
// Manual user-controlled conversational direction. These are NOT personalities,
// tones, or characters — they're destinations. The AI identity stays the same;
// only conversational priorities / pacing / progression direction adapt.
// Keep this list SMALL and CLEAN — exactly 5 modes.
export const DESTINATION_MODES = [
  { id: "vibes",    label: "Just Vibes",            tagline: "No pressure, no agenda. Keep it flowing." },
  { id: "number",   label: "Get Their Number",      tagline: "Move from app to phone. Light, intentional." },
  { id: "date",     label: "Get The Date",          tagline: "Steer toward meeting up. Concrete, confident." },
  { id: "real",     label: "Build Something Real",  tagline: "Depth, comfort, emotional investment." },
  { id: "afterhours", label: "Flirty / After Hours", tagline: "Higher tension, late-night energy, playful escalation." },
];

// Batch 2, item 1 — FEATURE FLAG (single source of truth on the frontend).
// The two experimental directions are hidden from the UI while this is false.
// Nothing is deleted: DESTINATION_MODES above still holds all five (kept for
// id/label lookups), the backend still knows the directions, and flipping this
// to true restores the chips everywhere. Mirrors the backend flag
// EXPERIMENTAL_DIRECTIONS_ENABLED in backend/core/conversation_state.py.
export const ENABLE_EXPERIMENTAL_DIRECTIONS = false;
export const EXPERIMENTAL_DIRECTION_IDS = ["real", "afterhours"];

// The directions the UI actually OFFERS — every chip renderer and every
// direction suggestion reads THIS list, so a hidden direction never renders
// and is never suggested. Full list when the flag is on.
export const VISIBLE_DESTINATION_MODES = ENABLE_EXPERIMENTAL_DIRECTIONS
  ? DESTINATION_MODES
  : DESTINATION_MODES.filter((d) => !EXPERIMENTAL_DIRECTION_IDS.includes(d.id));

export const DEFAULT_DESTINATION = "vibes";

// P1 — Goal/Direction Unification.
// The Destination control ("Direction") is now the single visible conversation
// direction control. The legacy backend `goal` param is derived from it via a
// simple static map (no API change). DO NOT remove — backend still expects `goal`.
export const DIRECTION_TO_GOAL_ID = {
  vibes: "vibes",
  number: "date",
  date: "date",
  real: "real",
  afterhours: "flirty",
};

export const DIRECTION_GOAL_LABELS = {
  // NOTE: this legacy `goal` LABEL rides into the prompt as free text
  // ("User goal: ..."). The authoritative direction is still the `destination`
  // id, which the backend maps independently via _destination_to_goal
  // (number -> GET_THEIR_NUMBER). Each label must match its own direction.
  vibes: "Just Vibes",
  number: "Get Their Number",
  date: "Get The Date",
  real: "Build Something Real",
  afterhours: "Flirty Escalation",
};

// Returns the legacy goal LABEL to send to the backend for a given Direction id.
export const getGoalForDirection = (destination) =>
  DIRECTION_GOAL_LABELS[destination] || "Just Vibes";

// =============================================================================
// Destination Progress — FRONTEND PRESENTATION LAYER ONLY.
// Translates the backend destination-engine telemetry (conversation_goal,
// destination_stage, destination_next_stage, destination_advancement,
// closing_readiness) into user-friendly language. NO engine terminology is
// ever shown. NO backend/AI behavior is affected — this only renders data that
// is already returned in every generation response.
// =============================================================================

// Friendly title per conversation_goal (backend enum -> user label).
export const DESTINATION_GOAL_TITLES = {
  JUST_VIBES: "Just Vibing",
  GET_THEIR_NUMBER: "Getting Their Number",
  GET_THE_DATE: "Getting The Date",
  BUILD_SOMETHING_REAL: "Building Something Real",
  AFTER_HOURS: "Keeping It Flirty",
};

// Ordered ladders per goal (mirrors DESTINATION_ENGINE_LADDERS in the backend)
// with internal stage -> user-facing label. Order is the source of truth for
// "Step X of Y" and the stepper.
export const DESTINATION_LADDERS = {
  JUST_VIBES: [
    { stage: "Connect", label: "Breaking the ice" },
    { stage: "Engage", label: "Building rapport" },
    { stage: "Flow", label: "Vibing" },
  ],
  GET_THEIR_NUMBER: [
    { stage: "Build Interest", label: "Sparking interest" },
    { stage: "Engagement", label: "Building connection" },
    { stage: "Move Off App", label: "Taking it off-app" },
  ],
  GET_THE_DATE: [
    { stage: "Build Interest", label: "Sparking interest" },
    { stage: "Real World Frame", label: "Setting up the meetup" },
    { stage: "Date Suggestion", label: "Asking them out" },
  ],
  BUILD_SOMETHING_REAL: [
    { stage: "Attraction", label: "Creating spark" },
    { stage: "Comfort", label: "Building comfort" },
    { stage: "Connection", label: "Going deeper" },
    { stage: "Commitment", label: "Getting serious" },
  ],
  AFTER_HOURS: [
    { stage: "Playful", label: "Keeping it playful" },
    { stage: "Tension", label: "Building tension" },
    { stage: "Flirt Investment", label: "Turning up the flirt" },
  ],
};

// Title-case fallback so an unmapped/new stage never renders blank.
export const friendlyStageLabel = (goal, stage) => {
  if (!stage) return null;
  const ladder = DESTINATION_LADDERS[goal] || [];
  const found = ladder.find((s) => s.stage === stage);
  if (found) return found.label;
  return stage.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

// destination_advancement -> { icon, color, line(nextLabel) }. Engine terms hidden.
export const ADVANCEMENT_COPY = {
  ADVANCE: { icon: "trending-up", color: "green", line: (next) => next ? `Good momentum — start steering toward ${next.toLowerCase()}.` : "Good momentum — keep leading." },
  STAY: { icon: "minus", color: "gold", line: () => "Hold here and keep it warm before pushing." },
  REGRESS: { icon: "trending-down", color: "amber", line: () => "Ease off — rebuild a little before moving forward." },
  HOLD_TERMINAL: { icon: "star", color: "gold", line: () => "You're there — go for it, no overthinking." },
};

// closing_readiness -> overlay strategy (Number/Date only). null = no overlay.
export const CLOSING_COPY = {
  READY: { strength: "strong", icon: "flame", color: "gold", title: "This is your window", line: "Warmth supports it — make your move now." },
  MISSED: { strength: "soft", icon: "clock", color: "amber", title: null, line: "Window passed — re-warm before trying again." },
  MAYBE_READY: { strength: "hint", icon: null, color: "gold", title: null, line: "Almost — one more warm exchange." },
  // NOT_READY intentionally omitted (no overlay; normal progress shows).
};

// =============================================================================
// B1 — Richer Destination Progress VISUALIZATION (display layer only).
// The backend engine still drives everything; it exposes a read-only continuous
// `destination_depth` (0..~18). We slice that depth into finer visual milestones
// so a smoothly-deepening conversation reads as steady progress instead of a
// coarse 3-step "frozen → jump → pinned" feel. Each milestone ROLLS UP to one of
// the existing engine stages — the `minDepth` group boundaries are aligned to the
// backend thresholds (3-stage goals: 4.0 / 8.0; BUILD_SOMETHING_REAL: 3.5/7/10.5)
// so the milestone's parentStage always equals the engine's computed stage.
// NO engine / threshold / decision logic is changed by anything here.
// =============================================================================
export const DESTINATION_MAX_DEPTH = 18; // formula ceiling (visual top band)

export const DESTINATION_FINE_MILESTONES = {
  GET_THE_DATE: [
    { label: "Initial interest", parentStage: "Build Interest", minDepth: 0 },
    { label: "Comfortable chat", parentStage: "Build Interest", minDepth: 2 },
    { label: "Real curiosity", parentStage: "Real World Frame", minDepth: 4 },
    { label: "Mutual investment", parentStage: "Real World Frame", minDepth: 6 },
    { label: "Real-world framing", parentStage: "Date Suggestion", minDepth: 8 },
    { label: "Date readiness", parentStage: "Date Suggestion", minDepth: 13 },
  ],
  GET_THEIR_NUMBER: [
    { label: "Initial interest", parentStage: "Build Interest", minDepth: 0 },
    { label: "Comfortable chat", parentStage: "Build Interest", minDepth: 2 },
    { label: "Building rapport", parentStage: "Engagement", minDepth: 4 },
    { label: "Mutual investment", parentStage: "Engagement", minDepth: 6 },
    { label: "Ready to connect", parentStage: "Move Off App", minDepth: 8 },
    { label: "Time to exchange", parentStage: "Move Off App", minDepth: 13 },
  ],
  AFTER_HOURS: [
    { label: "Light & playful", parentStage: "Playful", minDepth: 0 },
    { label: "Warming up", parentStage: "Playful", minDepth: 2 },
    { label: "Building tension", parentStage: "Tension", minDepth: 4 },
    { label: "Real chemistry", parentStage: "Tension", minDepth: 6 },
    { label: "Strong pull", parentStage: "Flirt Investment", minDepth: 8 },
    { label: "All-in flirt", parentStage: "Flirt Investment", minDepth: 13 },
  ],
  JUST_VIBES: [
    { label: "Breaking ice", parentStage: "Connect", minDepth: 0 },
    { label: "Finding rhythm", parentStage: "Connect", minDepth: 2 },
    { label: "Good banter", parentStage: "Engage", minDepth: 4 },
    { label: "Real connection", parentStage: "Engage", minDepth: 6 },
    { label: "Easy flow", parentStage: "Flow", minDepth: 8 },
    { label: "Totally in sync", parentStage: "Flow", minDepth: 13 },
  ],
  BUILD_SOMETHING_REAL: [
    { label: "Initial spark", parentStage: "Attraction", minDepth: 0 },
    { label: "Real interest", parentStage: "Attraction", minDepth: 2 },
    { label: "Building comfort", parentStage: "Comfort", minDepth: 3.5 },
    { label: "Feeling safe", parentStage: "Comfort", minDepth: 5 },
    { label: "Genuine connection", parentStage: "Connection", minDepth: 7 },
    { label: "Deeper bond", parentStage: "Connection", minDepth: 9 },
    { label: "Something real", parentStage: "Commitment", minDepth: 10.5 },
  ],
};

// Pure display helper. Given the goal + read-only telemetry, returns the finer
// visual journey. depth drives position; close-ready / terminal lifts to the top
// milestone so the stepper agrees with the close-ready alert (fixes the Q5
// "Step 1 stepper + make-your-move alert" adjacency). Falls back to the engine
// stage (first milestone of that group) when depth is absent (old cached results).
export const computeFineProgress = ({ goal, depth, advancement, closingReadiness, stage } = {}) => {
  const milestones = DESTINATION_FINE_MILESTONES[goal];
  if (!milestones || !milestones.length) return null;
  const last = milestones.length - 1;

  let fineIdx;
  if (typeof depth === "number" && !Number.isNaN(depth)) {
    fineIdx = 0;
    for (let i = 0; i < milestones.length; i++) {
      if (depth >= milestones[i].minDepth) fineIdx = i;
    }
  } else if (stage) {
    // Fallback: land on the first milestone of the engine stage's group.
    const i = milestones.findIndex((m) => m.parentStage === stage);
    fineIdx = i === -1 ? 0 : i;
  } else {
    return null; // no usable signal — caller renders nothing
  }

  // Terminal / close-ready lift (display-only Q5 fix).
  const isCloseGoal = goal === "GET_THE_DATE" || goal === "GET_THEIR_NUMBER";
  if (advancement === "HOLD_TERMINAL") fineIdx = last;
  if (isCloseGoal && closingReadiness === "READY") fineIdx = last;

  return {
    milestones,
    fineIdx,
    total: milestones.length,
    currentLabel: milestones[fineIdx].label,
    parentStage: milestones[fineIdx].parentStage,
  };
};


// =============================================================================
// P.7 — Persistent Progression & Momentum Separation (flag PERSISTENT_PROGRESSION).
// Progression = highest relationship stage reached this conversation (only moves
// forward, persists per session per goal until the conversation is reset).
// Momentum = live conversational energy with exactly TWO states (no red).
// Display/state layer only — the engine is untouched.
// =============================================================================
export const P7_MOMENTUM_META = {
  strong: { color: "green", label: "Strong Momentum", line: "The conversation is healthy and moving forward." },
  softening: { color: "yellow", label: "Momentum Softening", line: "Energy has cooled slightly — adjust your approach before pushing further." },
};

export const p7MomentumState = (momentum) =>
  momentum?.classification === "declining" ? "softening" : "strong";

// Compute the session's progression high-water mark for the active goal.
// Returns { peakMap, display } or null when no usable destination signal.
// peakMap is persisted on the session; display rides on the results object.
export const computeP7Peak = (data, sessionId) => {
  const goal = data?.conversation_goal;
  if (!goal || !DESTINATION_FINE_MILESTONES[goal]) return null;
  const fine = computeFineProgress({
    goal,
    depth: data.destination_depth,
    advancement: data.destination_advancement,
    closingReadiness: data.closing_readiness,
    stage: data.destination_stage,
  });
  if (!fine) return null;
  const session = sessionId ? getSessions().find((s) => s.id === sessionId) : null;
  const prevMap = (session && session.progressionPeak) || {};
  const prev = prevMap[goal] || null;
  const peakIdx = Math.max(fine.fineIdx, prev ? prev.fineIdx || 0 : 0);
  const depth = typeof data.destination_depth === "number" ? data.destination_depth : 0;
  const peakDepth = Math.max(depth, prev ? prev.depth || 0 : 0);
  return {
    peakMap: { ...prevMap, [goal]: { fineIdx: peakIdx, depth: peakDepth } },
    display: { fineIdx: peakIdx, heldAtPeak: peakIdx > fine.fineIdx },
  };
};

// Stage-specific goals

export const TONES = [
  { id: "smooth", label: "Smooth" },
  { id: "playful", label: "Playful" },
  { id: "bold", label: "Bold" },
];

// Batch 58 — "She last messaged" chips. `id` is the backend gap tier
// (core/conversation_state.GAP_TIERS); the last two are the only ones that
// trigger the re-open coaching, so the split sits deliberately at three days:
// a same-day or two-day gap is ordinary texting rhythm, not something to
// coach around.
export const GAP_TIERS = [
  { id: "none", label: "Just now" },
  { id: "under_a_day", label: "Today" },
  // Was "Yesterday", which named one day while the id spans three. The two-day
  // case has to live here: mapping it up would fire the re-open coaching on
  // ordinary texting rhythm, which conversation_state.py rejects by name.
  { id: "one_to_three_days", label: "A day or two" },
  { id: "three_to_seven_days", label: "A few days ago" },
  { id: "over_a_week", label: "A week+" },
];
export const DEFAULT_GAP_TIER = "none";

// Prefill for the chip row, from the session's `updatedAt`.
//
// MEASURED, and the thing to know before moving a boundary: only the THREE-DAY
// line changes what the engine produces. `none`, `under_a_day` and
// `one_to_three_days` yield byte-identical prompts, and `three_to_seven_days`
// and `over_a_week` yield identical coaching as each other. Five chips, one
// decision — the rest is display.
//
// Calendar days, not elapsed hours, because the labels are calendar words.
// Last used at 11pm and reopened at 1am is two hours elapsed but *yesterday*
// to the person reading the chip.
//
// This is a GUESS, and deliberately a visible one. `updatedAt` is when
// SparkCue was last used on this conversation, not when she last messaged —
// the app has never had her message times (see the note on GAP_TIERS above).
// That is why the row stays on screen with every chip selectable rather than
// collapsing into a derived value the user cannot see or argue with.
//
// Anything unusable — no timestamp, unparseable, or a future one from clock
// skew across devices — returns DEFAULT_GAP_TIER rather than inventing a gap.
export const gapTierFromTimestamp = (updatedAt, now = Date.now()) => {
  if (!updatedAt) return DEFAULT_GAP_TIER;
  const then = new Date(updatedAt);
  const nowDate = new Date(now);
  if (isNaN(then.getTime()) || isNaN(nowDate.getTime())) return DEFAULT_GAP_TIER;
  const midnight = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((midnight(nowDate) - midnight(then)) / 86400000);
  if (days < 0) return DEFAULT_GAP_TIER;           // clock skew
  if (days === 0) return (now - then.getTime()) < 3600000 ? "none" : "under_a_day";
  if (days <= 2) return "one_to_three_days";
  if (days <= 7) return "three_to_seven_days";
  return "over_a_week";
};

export const STAGE_LABELS = {
  first_time: "First Time Texting",
  already_talking: "Already Talking",
  restarting: "Restarting Conversation",
  soft_reset: "Picking Back Up",
  not_sure: "Analyzing...",
};

export const SIGNAL_COLORS = {
  high: "bg-green-500",
  moderate: "bg-yellow-500",
  unclear: "bg-red-500",
  unknown: "bg-gray-500",
};

export const STORAGE_KEYS = {
  sessions: "wingman_sessions",
  sessionsBackup: "wingman_sessions_backup",
  currentSessionId: "wingman_current_session_id",
  stats: "wingman_stats",
  pendingJob: "wingman_pending_job",
  outcomes: "wingman_outcomes",
  outcomesBackup: "wingman_outcomes_backup",
  missingFields: "wingman_missing_fields",
  missingFieldsBackup: "wingman_missing_fields_backup",
  readinessTelemetry: "wingman_readiness_telemetry",
  readinessTelemetryBackup: "wingman_readiness_telemetry_backup",
  // These four were dereferenced from the day they were written and never
  // added here. STORAGE_KEYS.userProfile was undefined, the Storage API
  // stringifies keys, and so the user profile and the clarification telemetry
  // both wrote to one localStorage key literally named "undefined" - primary
  // and backup included. hydrateStorage writes the profile and then writes the
  // clarification array over it, so first_name, looking_for and the whole
  // about_me map were destroyed on every reload. The names below are the ones
  // the surrounding code always assumed: IdentityNameField's comment already
  // said "the existing wingman_user_profile store".
  userProfile: "wingman_user_profile",
  userProfileBackup: "wingman_user_profile_backup",
  clarificationEvents: "wingman_clarification_events",
  clarificationEventsBackup: "wingman_clarification_events_backup",
  draft: "wingman_draft",
  deletedIds: "wingman_deleted_ids",
  onboardingSeen: "wingman_onboarding_seen",
  coachmarkCopy: "wingman_coachmark_copy_seen",
  coachmarkContinue: "wingman_coachmark_continue_seen",
};

// P2 — Onboarding: a realistic scripted sample conversation used by the
// "Try a sample" path so a first-time user sees a strong result with zero typing.
export const SAMPLE_CONVERSATION =
  "Me: haha that's actually a solid take, didn't expect that\n" +
  "Her: lol I'm full of surprises\n" +
  "Me: clearly. so what's your go-to when you actually wanna unwind?\n" +
  "Her: honestly just a good coffee spot and people watching";

// P3 — Signal Check: maps backend signalCheck.level -> color + interest word.
export const SIGNAL_LEVEL_META = {
  high: { color: "green", word: "Strong interest" },
  moderate: { color: "yellow", word: "Mixed signals" },
  low: { color: "red", word: "Low interest" },
  unclear: { color: "gray", word: "Unclear read" },
};

export const PSYCHOLOGY_LINES = [
  "Don't overthink it. Send something solid.",
  "Most people mess this up -- don't be that guy.",
  "One good reply can change the whole vibe.",
];

export const generateId = () => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

export const getInitialStats = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEYS.stats);
    if (saved) return JSON.parse(saved);
  } catch (e) { /* corrupt/unavailable — fall through to defaults */ }
  return { numberOfGenerations: 0, numberOfContinuations: 0, sessionStart: Date.now() };
};

// =============================================================================
// Persistence Reliability — durable storage layer (IndexedDB-backed).
//
// Architecture (3 layers, lowest-risk migration):
//  1. IN-MEMORY CACHE — the synchronous runtime source of truth. Once seeded,
//     reads/writes are instant and can NEVER be lost mid-session.
//  2. localStorage MIRROR (primary + backup keys) — fast synchronous boot snapshot
//     so the first paint shows data before async IndexedDB hydration completes.
//  3. IndexedDB (`wingman_db`) — the DURABLE store: large quota (fixes the
//     QuotaExceededError that silently dropped long chats) and survives reload.
//
// `hydrateStorage()` (async, called once on boot) reconciles IndexedDB <->
// localStorage by UNION-merging on id (newer wins), so whichever store survived
// an eviction repopulates the other — self-healing across both stores.
//
// All existing helpers (upsertSession / patchSessionState / removeSession /
// renameSession) keep working unchanged because they go through getSessions /
// saveSessions, whose signatures are identical (still synchronous).
// =============================================================================

const _isArray = (v) => Array.isArray(v);

// In-memory caches (null = not yet seeded).
let _sessionsCache = null;
let _outcomesCache = null;
let _missingCache = null;
let _readinessCache = null;
let _profileCache = null;
let _clarifyCache = null;

// Test-only: reset the in-memory caches (jsdom localStorage.clear() cannot).
export const __resetStorageCaches = () => {
  _sessionsCache = null;
  _outcomesCache = null;
  _missingCache = null;
  _readinessCache = null;
  _profileCache = null;
  _clarifyCache = null;
};

const _readLocalArray = (key) => {
  try {
    const saved = localStorage.getItem(key);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (_isArray(parsed)) return parsed;
    }
  } catch (e) { /* corrupt/unavailable */ }
  return null;
};

const _readLocalObject = (key) => {
  try {
    const saved = localStorage.getItem(key);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) return parsed;
    }
  } catch (e) { /* corrupt/unavailable */ }
  return null;
};

// Coerce a timestamp (number ms, ISO string, or missing) to a comparable number.
const _ts = (v) => {
  if (typeof v === "number") return Number.isNaN(v) ? 0 : v;
  const t = new Date(v || 0).getTime();
  return Number.isNaN(t) ? 0 : t;
};

// Union-merge two arrays of {id, updatedAt/createdAt}: keep the newer copy per id,
// most-recent first. Neither input can cause data loss (it is a union).
const _mergeById = (a, b) => {
  const byId = new Map();
  const add = (list) => {
    (list || []).forEach((s) => {
      if (!s || !s.id) return;
      const prev = byId.get(s.id);
      if (!prev) { byId.set(s.id, s); return; }
      const sa = _ts(s.updatedAt || s.createdAt);
      const sb = _ts(prev.updatedAt || prev.createdAt);
      byId.set(s.id, sa >= sb ? s : prev);
    });
  };
  add(b);
  add(a);
  return Array.from(byId.values()).sort(
    (x, y) => _ts(y.updatedAt || y.createdAt) - _ts(x.updatedAt || x.createdAt)
  );
};

// Union-merge outcome rows by anon_id (no timestamp ordering needed).
const _mergeOutcomes = (a, b) => {
  const byId = new Map();
  [...(b || []), ...(a || [])].forEach((o) => {
    if (o && o.anon_id) byId.set(o.anon_id, o);
  });
  return Array.from(byId.values());
};

// Union-merge missing-field events by id.
const _mergeMissing = (a, b) => {
  const byId = new Map();
  [...(b || []), ...(a || [])].forEach((e) => {
    if (e && e.id) byId.set(e.id, e);
  });
  return Array.from(byId.values());
};

// Union-merge closing-readiness telemetry events by id.
const _mergeReadiness = (a, b) => {
  const byId = new Map();
  [...(b || []), ...(a || [])].forEach((e) => {
    if (e && e.id) byId.set(e.id, e);
  });
  return Array.from(byId.values());
};

// Field-level merge of the small user-profile object: per field keep the copy
// with the newer set_at. Never loses a field present in either copy.
const _mergeProfile = (a, b) => {
  const out = {};
  const keys = new Set([...Object.keys(a || {}), ...Object.keys(b || {})]);
  keys.forEach((k) => {
    const va = (a || {})[k];
    const vb = (b || {})[k];
    if (va && vb && va.set_at && vb.set_at) out[k] = _ts(va.set_at) >= _ts(vb.set_at) ? va : vb;
    else out[k] = va || vb;
  });
  return out;
};

// --- Deletion tombstones (FM-2) ---------------------------------------------
// Union-merge hydration has no concept of deletion: a session deleted just
// before the app closed can still live in IndexedDB (the durable flush is async)
// and get resurrected on the next boot. A small, capped, durable tombstone set
// of deleted ids is subtracted during hydration to prevent that.
const _DELETED_CAP = 1000;

export const getDeletedIds = () => {
  const v = _readLocalArray(STORAGE_KEYS.deletedIds);
  return _isArray(v) ? v : [];
};

const _saveDeletedIds = (ids) => {
  const capped = ids.slice(-_DELETED_CAP);
  try { localStorage.setItem(STORAGE_KEYS.deletedIds, JSON.stringify(capped)); } catch (e) { /* ignore */ }
  idbSet(STORAGE_KEYS.deletedIds, capped).catch(() => {});
};

export const addDeletedId = (id) => {
  if (!id) return;
  const set = new Set(getDeletedIds());
  if (set.has(id)) return;
  set.add(id);
  _saveDeletedIds(Array.from(set));
};

// A resurrected/re-added id (undo, import) is no longer "deleted".
export const removeDeletedId = (id) => {
  if (!id) return;
  const next = getDeletedIds().filter((x) => x !== id);
  _saveDeletedIds(next);
};

// --- Cross-tab refresh (FM-3) -----------------------------------------------
// The `storage` event fires in OTHER tabs; this tab's in-memory cache is stale.
// Force a re-seed from localStorage so the list reflects the other tab's write.
export const reloadSessionsCache = () => {
  _sessionsCache = null;
  return getSessions();
};

// --- One-time duplicate cleanup (FM-4) --------------------------------------
// Collapses sessions that share BYTE-IDENTICAL message content (the artifact of
// the FM-1 stale-identity bug), keeping the most-recently-updated copy and
// salvaging its outcome/rpl + earliest createdAt. Returns { deduped, removedIds }.
const _dedupeByMessages = (sessions) => {
  const seen = new Map(); // messages -> kept session
  const kept = [];
  const removedIds = [];
  [...(sessions || [])]
    .sort((a, b) => _ts(b.updatedAt || b.createdAt) - _ts(a.updatedAt || a.createdAt))
    .forEach((s) => {
      const key = (s.messages || "").trim();
      if (!key) { kept.push(s); return; } // never merge empty/placeholder chats
      const prior = seen.get(key);
      if (!prior) {
        seen.set(key, s);
        kept.push(s);
      } else {
        // Salvage data onto the kept (newer) copy; drop the older duplicate.
        if (!prior.outcome && s.outcome) prior.outcome = s.outcome;
        if (!prior.rpl && s.rpl) prior.rpl = s.rpl;
        const pc = _ts(prior.createdAt) || Infinity;
        const sc = _ts(s.createdAt) || Infinity;
        prior.createdAt = Math.min(pc, sc);
        removedIds.push(s.id);
      }
    });
  return { deduped: kept, removedIds };
};

// --- Sessions ---------------------------------------------------------------

// Synchronous read from the in-memory cache (seeded from localStorage on first
// call, before async hydration). Backup-key recovery preserved. Never throws.
export const getSessions = () => {
  if (_sessionsCache !== null) return _sessionsCache;
  _sessionsCache =
    _readLocalArray(STORAGE_KEYS.sessions) ||
    _readLocalArray(STORAGE_KEYS.sessionsBackup) ||
    [];
  return _sessionsCache;
};

// Synchronous write: update memory (never lost) + localStorage mirror + flush to
// IndexedDB (async, durable). Returns false only when the data is genuinely at
// risk (unserializable, OR localStorage failed AND IndexedDB is unavailable).
export const saveSessions = (sessions) => {
  const arr = _isArray(sessions) ? sessions : [];
  _sessionsCache = arr; // runtime source of truth — cannot be lost this session
  let serialized;
  try {
    serialized = JSON.stringify(arr);
  } catch (e) {
    return false;
  }
  let localOk = true;
  try {
    localStorage.setItem(STORAGE_KEYS.sessions, serialized);
  } catch (e) {
    localOk = false; // quota/unavailable — harmless if IndexedDB is available
  }
  try { localStorage.setItem(STORAGE_KEYS.sessionsBackup, serialized); } catch (e) { /* ignore */ }
  // Durable flush (fire-and-forget; large quota so long chats persist).
  idbSet(STORAGE_KEYS.sessions, arr).catch(() => {});
  return localOk || isIdbAvailable();
};

// One-time async boot reconciliation: union-merge IndexedDB <-> localStorage so
// whichever store survived an eviction repopulates the cache (and the other
// store). Resolves to the reconciled { sessions, outcomes }.
export const hydrateStorage = async () => {
  // Sessions
  const localSessions =
    _readLocalArray(STORAGE_KEYS.sessions) ||
    _readLocalArray(STORAGE_KEYS.sessionsBackup) ||
    [];
  let idbSessions = null;
  try {
    const v = await idbGet(STORAGE_KEYS.sessions);
    if (_isArray(v)) idbSessions = v;
  } catch (e) { /* unavailable */ }
  let mergedSessions = _mergeById(localSessions, idbSessions);
  // FM-2: drop any session resurrected from a lagging IndexedDB write after delete.
  const tombstoned = new Set(getDeletedIds());
  if (tombstoned.size) mergedSessions = mergedSessions.filter((s) => !tombstoned.has(s.id));
  // FM-4: one-time conservative duplicate cleanup, with a reversible backup.
  try {
    if (!localStorage.getItem("wingman_dedup_done")) {
      await idbSet("wingman_sessions_precleanup_backup", { sessions: mergedSessions, at: new Date().toISOString() });
      const { deduped, removedIds } = _dedupeByMessages(mergedSessions);
      if (removedIds.length) {
        mergedSessions = deduped;
        removedIds.forEach(addDeletedId); // keep dropped dupes from resurrecting
      }
      localStorage.setItem("wingman_dedup_done", "1");
    }
  } catch (e) { /* cleanup is best-effort; never block boot */ }
  _sessionsCache = mergedSessions;
  // Write back so both stores converge (mirror + IndexedDB).
  saveSessions(mergedSessions);

  // Outcomes
  const localOutcomes =
    _readLocalArray(STORAGE_KEYS.outcomes) ||
    _readLocalArray(STORAGE_KEYS.outcomesBackup) ||
    [];
  let idbOutcomes = null;
  try {
    const v = await idbGet(STORAGE_KEYS.outcomes);
    if (_isArray(v)) idbOutcomes = v;
  } catch (e) { /* unavailable */ }
  const mergedOutcomes = _mergeOutcomes(localOutcomes, idbOutcomes);
  _outcomesCache = mergedOutcomes;
  saveOutcomes(mergedOutcomes);

  // Missing-field events (union by id, content-free)
  const localMissing =
    _readLocalArray(STORAGE_KEYS.missingFields) ||
    _readLocalArray(STORAGE_KEYS.missingFieldsBackup) ||
    [];
  let idbMissing = null;
  try {
    const v = await idbGet(STORAGE_KEYS.missingFields);
    if (_isArray(v)) idbMissing = v;
  } catch (e) { /* unavailable */ }
  const mergedMissing = _mergeMissing(localMissing, idbMissing);
  _missingCache = mergedMissing;
  saveMissingFieldEvents(mergedMissing);

  // Closing-readiness telemetry events (union by id, content-free)
  const localReadiness =
    _readLocalArray(STORAGE_KEYS.readinessTelemetry) ||
    _readLocalArray(STORAGE_KEYS.readinessTelemetryBackup) ||
    [];
  let idbReadiness = null;
  try {
    const v = await idbGet(STORAGE_KEYS.readinessTelemetry);
    if (_isArray(v)) idbReadiness = v;
  } catch (e) { /* unavailable */ }
  const mergedReadiness = _mergeReadiness(localReadiness, idbReadiness);
  _readinessCache = mergedReadiness;
  saveReadinessEvents(mergedReadiness);

  // User profile (Dynamic Clarification Phase 2) — small object, field-level merge by set_at.
  const localProfile = _readLocalObject(STORAGE_KEYS.userProfile) || _readLocalObject(STORAGE_KEYS.userProfileBackup) || {};
  let idbProfile = null;
  try {
    const v = await idbGet(STORAGE_KEYS.userProfile);
    if (v && typeof v === "object" && !Array.isArray(v)) idbProfile = v;
  } catch (e) { /* unavailable */ }
  const mergedProfile = _mergeProfile(localProfile, idbProfile);
  _profileCache = mergedProfile;
  saveUserProfile(mergedProfile);

  // Clarification telemetry events (union by id, content-free).
  const localClarify =
    _readLocalArray(STORAGE_KEYS.clarificationEvents) ||
    _readLocalArray(STORAGE_KEYS.clarificationEventsBackup) || [];
  let idbClarify = null;
  try {
    const v = await idbGet(STORAGE_KEYS.clarificationEvents);
    if (_isArray(v)) idbClarify = v;
  } catch (e) { /* unavailable */ }
  const mergedClarify = _mergeReadiness(localClarify, idbClarify);
  _clarifyCache = mergedClarify;
  saveClarificationEvents(mergedClarify);

  return { sessions: _sessionsCache, outcomes: _outcomesCache, missing: _missingCache, readiness: _readinessCache, profile: _profileCache, clarify: _clarifyCache };
};

// --- Fresh read-merge-write mutation helpers (single source of truth) ---
// Each returns the resulting sessions array so callers can mirror it into React
// state. `ok` reflects whether the write actually persisted.

// Insert (prepend) or update a full session object by id.
export const upsertSession = (session) => {
  const all = getSessions();
  removeDeletedId(session.id); // a saved id is alive again (covers re-create/import)
  const idx = all.findIndex((s) => s.id === session.id);
  let next;
  if (idx === -1) {
    next = [session, ...all]; // new sessions prepend (most recent first)
  } else {
    next = [...all];
    next[idx] = { ...all[idx], ...session };
  }
  const ok = saveSessions(next);
  return { sessions: next, ok };
};

// Merge a partial state patch into an existing session's `state` (no-op if absent).
export const patchSessionState = (id, statePatch) => {
  const all = getSessions();
  const idx = all.findIndex((s) => s.id === id);
  if (idx === -1) return { sessions: all, ok: true, changed: false };
  const existing = all[idx];
  const next = [...all];
  next[idx] = {
    ...existing,
    state: { ...(existing.state || {}), ...statePatch },
    updatedAt: Date.now(),
  };
  const ok = saveSessions(next);
  return { sessions: next, ok, changed: true };
};

// Remove a session by id.
export const removeSession = (id) => {
  const all = getSessions();
  const next = all.filter((s) => s.id !== id);
  addDeletedId(id); // FM-2: tombstone so a lagging IndexedDB copy can't resurrect it
  const ok = saveSessions(next);
  return { sessions: next, ok };
};

// Rename a session by id (contactName).
export const renameSession = (id, name) => {
  const all = getSessions();
  const idx = all.findIndex((s) => s.id === id);
  if (idx === -1) return { sessions: all, ok: true };
  const next = [...all];
  next[idx] = { ...all[idx], contactName: name };
  const ok = saveSessions(next);
  return { sessions: next, ok };
};

// =============================================================================
// Export / Import — conversation backup (P1 data-safety vs iOS Safari 7-day
// localStorage eviction). Pure helpers; import goes through saveSessions so the
// crash-safe primary+backup layer is preserved.
// =============================================================================

export const EXPORT_VERSION = 1;

export const buildSessionsExport = (sessions, includeTelemetry = false) => {
  const base = {
    app: "wingman-ai",
    type: "wingman-conversations",
    version: EXPORT_VERSION,
    exportedAt: new Date().toISOString(),
    count: _isArray(sessions) ? sessions.length : 0,
    sessions: _isArray(sessions) ? sessions : [],
  };
  if (includeTelemetry) {
    // Phase-1 Dynamic Clarification read-out: top missing fields by occurrence.
    base.missingFieldStats = getMissingFieldStats();
    base.missingFieldEvents = getMissingFieldEvents();
    // Step 1 Closing Readiness shadow read-out.
    base.closingReadinessStats = getClosingReadinessStats();
    base.closingReadinessEvents = getReadinessEvents();
    // Phase 2 Dynamic Clarification read-out (content-free).
    base.clarificationStats = getClarificationStats();
    // Local user profile (Identity Input + intent) — included in full backup only.
    base.userProfile = getUserProfile();
  }
  return base;
};

// Trigger a browser download of a JSON object. Never throws.
export const downloadJson = (filename, obj) => {
  try {
    const blob = new Blob([JSON.stringify(obj, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    return true;
  } catch (e) {
    return false;
  }
};

const _dateStamp = () => new Date().toISOString().slice(0, 10);

// Export ALL saved conversations to one backup file.
export const exportAllSessions = () => {
  const sessions = getSessions();
  const ok = downloadJson(`sparkcue-conversations-${_dateStamp()}.json`, buildSessionsExport(sessions, true));
  return { ok, count: sessions.length };
};

// Export a single conversation.
export const exportSingleSession = (session) => {
  if (!session || !session.id) return { ok: false };
  const safeName = (session.contactName || "chat").replace(/[^\w-]+/g, "_").slice(0, 30) || "chat";
  const ok = downloadJson(`sparkcue-${safeName}-${_dateStamp()}.json`, buildSessionsExport([session]));
  return { ok };
};

// Parse an exported file (accepts {sessions:[...]} or a raw array) and merge
// into existing storage. Conflicts resolved by keeping the newer copy
// (updatedAt/createdAt). Returns { ok, sessions, added, updated, error }.
export const importSessionsFromText = (text) => {
  let parsed;
  try {
    parsed = JSON.parse(text);
  } catch (e) {
    return { ok: false, error: "Invalid JSON file." };
  }
  const incoming = _isArray(parsed)
    ? parsed
    : parsed && _isArray(parsed.sessions)
    ? parsed.sessions
    : null;
  if (!incoming) return { ok: false, error: "No conversations found in this file." };
  const valid = incoming.filter((s) => s && typeof s === "object" && s.id);
  if (valid.length === 0) return { ok: false, error: "No valid conversations to import." };

  const existing = getSessions();
  const byId = new Map(existing.map((s) => [s.id, s]));
  let added = 0;
  let updated = 0;
  for (const s of valid) {
    const prev = byId.get(s.id);
    if (!prev) {
      byId.set(s.id, s);
      added += 1;
    } else {
      const a = s.updatedAt || s.createdAt || 0;
      const b = prev.updatedAt || prev.createdAt || 0;
      if (a >= b) {
        byId.set(s.id, { ...prev, ...s });
        updated += 1;
      }
    }
  }
  const merged = Array.from(byId.values()).sort(
    (x, y) => (y.updatedAt || y.createdAt || 0) - (x.updatedAt || x.createdAt || 0)
  );
  // FM-2: importing an id is an intentional resurrection — clear any tombstone.
  valid.forEach((s) => removeDeletedId(s.id));
  const ok = saveSessions(merged);
  return {
    ok,
    sessions: merged,
    added,
    updated,
    error: ok ? null : "Storage full — could not save the imported conversations.",
  };
};

// =============================================================================
// Outcome Feedback System — Phase 1 (local-only, content-free).
// A tiny ground-truth loop: the user tags how a conversation actually went
// (4-bucket ordinal ladder). The tag + a content-free prediction snapshot
// (derived from the session's last generation) are stored INSIDE the session
// object in localStorage. NOTHING is transmitted. No text, no names. This
// powers later validation of the engine's predictions. NO engine/AI behavior
// is affected — this only reads telemetry already returned by the backend.
// =============================================================================

// One-tap canonical outcome ladder. `value` is a stable local id mapped 1:1 to the server's
// CANONICAL_OUTCOMES (see OUTCOME_VALUE_TO_CANONICAL); it is NOT an ordinal score. Display order is
// positive-first for a friendlier prompt. No new outcome types — these are the existing 6 canonical.
export const OUTCOME_OPTIONS = [
  { value: 2, id: "number", label: "Got their number", icon: "phone" },
  { value: 3, id: "date", label: "Date planned", icon: "calendar-heart" },
  { value: 1, id: "talking", label: "Still chatting", icon: "message-circle" },
  { value: 0, id: "ghosted", label: "Ghosted", icon: "ghost" },
  { value: 4, id: "faded", label: "Faded", icon: "wind" },
  { value: 5, id: "other", label: "Other", icon: "help-circle" },
];

// =============================================================================
// Phase 16 — Outcome Capture bridge. Persists the local one-tap outcome to the
// SERVER-SIDE capture store (alongside replay data) so future intelligence layers
// learn from real evidence. Fire-and-forget, never throws, never blocks the UI,
// and sends NO message content — only the session id + structured labels.
// =============================================================================
export const OUTCOME_VALUE_TO_CANONICAL = {
  0: "GHOSTED", 1: "STILL_CHATTING", 2: "GOT_NUMBER", 3: "DATE_PLANNED", 4: "FADED", 5: "OTHER",
};

export const postOutcomeToBackend = (session, outcomeValue) => {
  try {
    if (!session || !session.id) return;
    const canonical = OUTCOME_VALUE_TO_CANONICAL[outcomeValue];
    if (!canonical) return;
    const state = session.state || {};
    // OC.1 RC-3: prefer the last known real `turn_index` at generation time (persisted
    // on the session as `lastTurnIndex`), fall back to `state.exchangeCount` for pre-OC.1
    // sessions. Flag OFF preserves the original state.exchangeCount only path.
    const turnNumber = OUTCOME_CAPTURE_V2_ENABLED
      ? (typeof session.lastTurnIndex === "number"
          ? session.lastTurnIndex
          : (typeof state.exchangeCount === "number" ? state.exchangeCount : null))
      : (typeof state.exchangeCount === "number" ? state.exchangeCount : null);
    // OC.1 RC-1: forward the user's actual last incoming message when known.
    // Read-only from session; never invent. Flag OFF omits the field entirely.
    let actualUserResponse = null;
    if (OUTCOME_CAPTURE_V2_ENABLED) {
      const msgs = Array.isArray(session.messages) ? session.messages : [];
      // Session.messages here is a joined transcript string in some flows; be defensive.
      if (typeof msgs === "string") {
        const lines = msgs.split("\n").map((s) => s.trim()).filter(Boolean);
        for (let i = lines.length - 1; i >= 0; i--) {
          const ln = lines[i];
          if (!ln.includes(":")) continue;
          const label = ln.split(":", 1)[0].trim().toLowerCase();
          if (label !== "me" && label !== "you" && label !== "i") {
            actualUserResponse = ln.substring(ln.indexOf(":") + 1).trim() || null;
            break;
          }
          // hit an AI line first from the bottom => no "her" reply after AI => leave null
          break;
        }
      } else if (session.lastHerMessage && typeof session.lastHerMessage === "string") {
        actualUserResponse = session.lastHerMessage.trim() || null;
      }
    }
    fetch(`${API}/outcome`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        session_id: session.id,
        conversation_id: session.id,
        turn_number: turnNumber,
        selected_objective: state.destination || null,
        outcome: canonical,
        ...(OUTCOME_CAPTURE_V2_ENABLED && actualUserResponse
          ? { actual_user_response: actualUserResponse }
          : {}),
      }),
    }).catch(() => {});
  } catch (e) { /* never block the UI */ }
};


// A thread is eligible for the gentle one-time reopen prompt once it has been
// idle this long (and isn't tagged / dismissed yet).
export const OUTCOME_IDLE_MS = 24 * 3600000; // ~24h

// Coarse buckets reduce re-identification risk and are sufficient for the
// validation questions (per the MVP spec).
export const bucketDepth = (d) => {
  if (typeof d !== "number" || Number.isNaN(d)) return null;
  if (d < 4) return "0-3";
  if (d < 8) return "4-7";
  if (d < 13) return "8-12";
  return "13+";
};

export const bucketExchange = (n) => {
  if (typeof n !== "number" || Number.isNaN(n)) return null;
  if (n <= 2) return "0-2";
  if (n <= 5) return "3-5";
  if (n <= 10) return "6-10";
  return "11+";
};

export const bucketDays = (ms) => {
  if (typeof ms !== "number" || Number.isNaN(ms) || ms < 0) return null;
  const days = ms / 86400000;
  if (days < 1) return "<1";
  if (days <= 3) return "1-3";
  if (days <= 7) return "4-7";
  return "8+";
};

// Build the content-free prediction snapshot + outcome from a session. Reads
// ONLY structured telemetry already on the session (no message text, no name).
export const buildOutcomeRecord = (session, outcomeValue) => {
  const res = (session && session.results) || {};
  const state = (session && session.state) || {};
  const bestIdx = res.bestMoveIndex || 0;
  const tone = (res.replies && res.replies[bestIdx] && res.replies[bestIdx].style) || null;
  const baseTs = (session && (session.updatedAt || session.createdAt)) || Date.now();
  // RPL (shadow) pairing — content-free relationship-stage trajectory.
  const rpl =
    (session && session.rpl) ||
    computeRelationshipStage(session && session.messages, session && session.contactName, {
      closingReadiness: res.closing_readiness,
    });
  return {
    schema: 1,
    outcome: outcomeValue, // 0 died | 1 talking | 2 number | 3 date
    direction: state.destination || null,
    tone,
    signal_level: (res.signalCheck && res.signalCheck.level) || null,
    closing_readiness: res.closing_readiness || null,
    destination_stage: res.destination_stage || null,
    depth_bucket: bucketDepth(res.destination_depth),
    exchange_bucket: bucketExchange(state.exchangeCount),
    days_to_outcome: bucketDays(Date.now() - baseTs),
    // RPL shadow pairing (relationship-progression validation primitives).
    highest_relationship_stage_reached: rpl ? rpl.relationship_stage : null,
    final_relationship_stage: rpl ? rpl.current_turn_stage : null,
    turns_since_last_advancement: rpl ? rpl.turns_since_last_advancement : null,
    relationship_progression_state: rpl ? rpl.progression_state : null,
    tagged_at: new Date().toISOString().slice(0, 10),
  };
};

// Tag (or re-tag) a session's outcome. Read-merge-write; never throws.
export const setSessionOutcome = (id, outcomeValue) => {
  const all = getSessions();
  const idx = all.findIndex((s) => s.id === id);
  if (idx === -1) return { sessions: all, ok: true };
  const next = [...all];
  next[idx] = {
    ...all[idx],
    outcome: buildOutcomeRecord(all[idx], outcomeValue),
    outcomePromptDismissed: true,
  };
  const ok = saveSessions(next);
  return { sessions: next, ok };
};

// Dismiss the gentle reopen prompt without tagging (so it never nags again).
export const dismissOutcomePrompt = (id) => {
  const all = getSessions();
  const idx = all.findIndex((s) => s.id === id);
  if (idx === -1) return { sessions: all, ok: true };
  const next = [...all];
  next[idx] = { ...all[idx], outcomePromptDismissed: true };
  const ok = saveSessions(next);
  return { sessions: next, ok };
};

// Whether a freshly reopened session should show the one-time outcome chip:
// idle long enough, has a real prediction to validate, not tagged, not dismissed.
export const shouldPromptOutcome = (session) => {
  if (!session) return false;
  if (session.outcome) return false;
  if (session.outcomePromptDismissed) return false;
  if (!session.results || !session.results.replies) return false;
  const ts = session.updatedAt || session.createdAt || 0;
  return Date.now() - ts >= OUTCOME_IDLE_MS;
};

// "When the user returns to the app" trigger: from all local sessions, pick the single most-recently
// touched one that is eligible for the gentle outcome prompt (idle, untagged, not dismissed). Returns
// null when none qualify — so the prompt appears at most once per eligible thread and never nags.
export const pickPromptableSession = (sessions) => {
  const eligible = (sessions || []).filter(shouldPromptOutcome);
  if (!eligible.length) return null;
  return eligible.sort(
    (a, b) => (b.updatedAt || b.createdAt || 0) - (a.updatedAt || a.createdAt || 0)
  )[0];
};

// =============================================================================
// Hybrid C — Anonymous Outcome Retention (data-integrity fix).
// Deleting a conversation must not silently delete its (already content-free)
// outcome telemetry — that would bias the validation dataset (users delete
// ghosted/failed chats far more than successes). On delete of a TAGGED chat we
// preserve ONLY an anonymous, id-detached outcome record in a SEPARATE local
// store (`wingman_outcomes`). No conversation text, no name, no session id, no
// reconstructable content. Local-only, never synced, never transmitted, not
// user-facing. Untagged chats retain nothing.
// =============================================================================

// Safe read of the anonymous outcomes store (cache + backup recovery). Never throws.
export const getOutcomes = () => {
  if (_outcomesCache !== null) return _outcomesCache;
  _outcomesCache =
    _readLocalArray(STORAGE_KEYS.outcomes) ||
    _readLocalArray(STORAGE_KEYS.outcomesBackup) ||
    [];
  return _outcomesCache;
};

// Safe write of the anonymous outcomes store. Memory + localStorage mirror
// (primary + backup, parity with sessions) + durable IndexedDB flush. Never throws.
export const saveOutcomes = (outcomes) => {
  const arr = _isArray(outcomes) ? outcomes : [];
  _outcomesCache = arr;
  let serialized;
  try {
    serialized = JSON.stringify(arr);
  } catch (e) {
    return false;
  }
  let localOk = true;
  try { localStorage.setItem(STORAGE_KEYS.outcomes, serialized); } catch (e) { localOk = false; }
  try { localStorage.setItem(STORAGE_KEYS.outcomesBackup, serialized); } catch (e) { /* ignore */ }
  idbSet(STORAGE_KEYS.outcomes, arr).catch(() => {});
  return localOk || isIdbAvailable();
};

// --- Draft autosave (in-progress, un-generated turn) ------------------------
// Preserves the text the user is typing (their reply + your sent message) so a
// reload / backgrounding mid-turn does not lose it ("conversation rollback").
// Tiny payload — mirrored to localStorage + IndexedDB. Cleared on a successful
// generation, a new conversation, or switching to a different session.
export const saveDraft = (draft) => {
  if (!draft || (!String(draft.their || "").trim() && !String(draft.mine || "").trim())) {
    return clearDraft();
  }
  const payload = {
    sessionId: draft.sessionId || null,
    their: draft.their || "",
    mine: draft.mine || "",
    ts: Date.now(),
  };
  try { localStorage.setItem(STORAGE_KEYS.draft, JSON.stringify(payload)); } catch (e) { /* ignore */ }
  idbSet(STORAGE_KEYS.draft, payload).catch(() => {});
  return true;
};

export const getDraft = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEYS.draft);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (parsed && typeof parsed === "object") return parsed;
    }
  } catch (e) { /* ignore */ }
  return null;
};

export const clearDraft = () => {
  try { localStorage.removeItem(STORAGE_KEYS.draft); } catch (e) { /* ignore */ }
  idbSet(STORAGE_KEYS.draft, null).catch(() => {});
  return true;
};

// Async hydration of the draft from IndexedDB (covers a localStorage eviction).
export const hydrateDraft = async () => {
  const local = getDraft();
  if (local) return local;
  try {
    const v = await idbGet(STORAGE_KEYS.draft);
    if (v && typeof v === "object") {
      try { localStorage.setItem(STORAGE_KEYS.draft, JSON.stringify(v)); } catch (e) { /* ignore */ }
      return v;
    }
  } catch (e) { /* unavailable */ }
  return null;
};

// --- Dynamic Clarification (Phase 1) — missing-user-info frequency store -----
// Content-free events: { id, field, was_question, tagged_at }. NO message text.
// Durable (cache + localStorage mirror + backup + IndexedDB), local-only.

export const getMissingFieldEvents = () => {
  if (_missingCache !== null) return _missingCache;
  _missingCache =
    _readLocalArray(STORAGE_KEYS.missingFields) ||
    _readLocalArray(STORAGE_KEYS.missingFieldsBackup) ||
    [];
  return _missingCache;
};

export const saveMissingFieldEvents = (events) => {
  const arr = _isArray(events) ? events : [];
  _missingCache = arr;
  let serialized;
  try { serialized = JSON.stringify(arr); } catch (e) { return false; }
  let localOk = true;
  try { localStorage.setItem(STORAGE_KEYS.missingFields, serialized); } catch (e) { localOk = false; }
  try { localStorage.setItem(STORAGE_KEYS.missingFieldsBackup, serialized); } catch (e) { /* ignore */ }
  idbSet(STORAGE_KEYS.missingFields, arr).catch(() => {});
  return localOk || isIdbAvailable();
};

// Append detected gap events (each { field, was_question }) with id + date.
export const recordMissingFields = (fields) => {
  if (!_isArray(fields) || !fields.length) return { ok: true, events: getMissingFieldEvents() };
  const day = new Date().toISOString().slice(0, 10);
  const rows = fields
    .filter((f) => f && f.field)
    .map((f) => ({
      id: `mf_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
      field: f.field,
      was_question: !!f.was_question,
      tagged_at: day,
    }));
  const next = [...getMissingFieldEvents(), ...rows];
  const ok = saveMissingFieldEvents(next);
  return { ok, events: next };
};

// Aggregate: top missing fields by occurrence (the Phase-1 read-out).
export const getMissingFieldStats = () => {
  const events = getMissingFieldEvents();
  const counts = {};
  events.forEach((e) => {
    if (e && e.field) counts[e.field] = (counts[e.field] || 0) + 1;
  });
  const total = events.length;
  const top = Object.entries(counts)
    .map(([field, count]) => ({ field, count, pct: total ? Math.round((count / total) * 100) : 0 }))
    .sort((a, b) => b.count - a.count);
  return { total, top, generatedAt: new Date().toISOString() };
};

// --- Closing Readiness Shadow Telemetry (Step 1) -----------------------------
// Content-free events correlating the engine's closing_readiness with open
// qualification state + the fine progress step. NO message text, no names.
// Durable (cache + localStorage mirror + backup + IndexedDB), local-only.

export const getReadinessEvents = () => {
  if (_readinessCache !== null) return _readinessCache;
  _readinessCache =
    _readLocalArray(STORAGE_KEYS.readinessTelemetry) ||
    _readLocalArray(STORAGE_KEYS.readinessTelemetryBackup) ||
    [];
  return _readinessCache;
};

export const saveReadinessEvents = (events) => {
  const arr = _isArray(events) ? events : [];
  _readinessCache = arr;
  let serialized;
  try { serialized = JSON.stringify(arr); } catch (e) { return false; }
  let localOk = true;
  try { localStorage.setItem(STORAGE_KEYS.readinessTelemetry, serialized); } catch (e) { localOk = false; }
  try { localStorage.setItem(STORAGE_KEYS.readinessTelemetryBackup, serialized); } catch (e) { /* ignore */ }
  idbSet(STORAGE_KEYS.readinessTelemetry, arr).catch(() => {});
  return localOk || isIdbAvailable();
};

// Append one content-free readiness telemetry atom (id + date added here).
export const recordReadinessEvent = (event) => {
  if (!event || typeof event !== "object") return { ok: true, events: getReadinessEvents() };
  const row = {
    id: `cr_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
    ...event,
    tagged_at: new Date().toISOString().slice(0, 10),
  };
  const next = [...getReadinessEvents(), row];
  const ok = saveReadinessEvents(next);
  return { ok, events: next };
};

// Aggregate read-out: how often READY fires while qualification is still open,
// which fields are present when readiness fires, how often the final step shows
// while qualification is open, and how often READY precedes intent resolution.
export const getClosingReadinessStats = () => {
  const events = getReadinessEvents();
  const total = events.length;
  const pct = (n, d) => (d ? Math.round((n / d) * 100) : 0);

  const dist = { NOT_READY: 0, MAYBE_READY: 0, READY: 0, MISSED: 0, none: 0 };
  let readyCount = 0;
  let qualOpenCount = 0;
  let readyQualOpen = 0;
  let readyIntentOpen = 0;
  let step6Count = 0;
  let step6QualOpen = 0;
  const fieldsWhenReady = {};

  events.forEach((e) => {
    const r = e && e.closing_readiness;
    dist[r && dist[r] !== undefined ? r : "none"] += 1;
    if (e && e.qualification_open) qualOpenCount += 1;
    if (e && e.ready) {
      readyCount += 1;
      if (e.qualification_open) readyQualOpen += 1;
      if (e.intent_open) readyIntentOpen += 1;
      (e.open_fields || []).forEach((f) => { fieldsWhenReady[f] = (fieldsWhenReady[f] || 0) + 1; });
    }
    if (e && e.is_final_step) {
      step6Count += 1;
      if (e.qualification_open) step6QualOpen += 1;
    }
  });

  const readiness_distribution = Object.entries(dist).map(([state, count]) => ({
    state, count, pct: pct(count, total),
  }));
  const field_frequency_when_ready = Object.entries(fieldsWhenReady)
    .map(([field, count]) => ({ field, count, pct_of_ready: pct(count, readyCount) }))
    .sort((a, b) => b.count - a.count);

  return {
    total,
    ready_count: readyCount,
    readiness_distribution,
    qualification_open: { count: qualOpenCount, pct: pct(qualOpenCount, total) },
    ready_while_qualification_open: { count: readyQualOpen, pct_of_ready: pct(readyQualOpen, readyCount) },
    ready_before_intent_resolved: { count: readyIntentOpen, pct_of_ready: pct(readyIntentOpen, readyCount) },
    field_frequency_when_ready,
    final_step: { count: step6Count, pct: pct(step6Count, total) },
    final_step_while_qualification_open: { count: step6QualOpen, pct_of_final_step: pct(step6QualOpen, step6Count) },
    generatedAt: new Date().toISOString(),
  };
};

// --- User Profile (Dynamic Clarification Phase 2) ----------------------------
// Small durable object of one-time, user-supplied facts reused as generation
// context (Phase 1 scope: `looking_for` only). Local-only, never transmitted
// except as derived context text the user explicitly provided.

export const getUserProfile = () => {
  if (_profileCache !== null) return _profileCache;
  _profileCache = _readLocalObject(STORAGE_KEYS.userProfile) || _readLocalObject(STORAGE_KEYS.userProfileBackup) || {};
  return _profileCache;
};

export const saveUserProfile = (profile) => {
  const obj = profile && typeof profile === "object" && !Array.isArray(profile) ? profile : {};
  _profileCache = obj;
  let serialized;
  try { serialized = JSON.stringify(obj); } catch (e) { return false; }
  let localOk = true;
  try { localStorage.setItem(STORAGE_KEYS.userProfile, serialized); } catch (e) { localOk = false; }
  try { localStorage.setItem(STORAGE_KEYS.userProfileBackup, serialized); } catch (e) { /* ignore */ }
  idbSet(STORAGE_KEYS.userProfile, obj).catch(() => {});
  return localOk || isIdbAvailable();
};

export const setUserProfileField = (field, value) => {
  if (!field) return getUserProfile();
  const next = { ...getUserProfile(), [field]: { ...value, set_at: (value && value.set_at) || new Date().toISOString() } };
  saveUserProfile(next);
  return next;
};

export const clearUserProfileField = (field) => {
  const cur = { ...getUserProfile() };
  delete cur[field];
  saveUserProfile(cur);
  return cur;
};

// --- About-me memory (Phase 7b) ---------------------------------------------
// The real-world facts the new core (Phase 5) asks for and then never asks
// again: job / age / city / car / availability / hobbies. Stored on-device
// inside the same durable user-profile object (localStorage + IndexedDB +
// backup), under `about_me` as a flat {key: "plain text"} map, and forwarded
// with every generate request so the backend stops re-asking what it knows.
// "Data stays on this device" — the only thing transmitted is the fact text
// the user themselves typed, exactly as the intent-context already is.
export const ABOUT_ME_KEYS = ["job", "age", "city", "car", "availability", "hobbies"];
export const ABOUT_ME_LABELS = {
  job: "Job / work", age: "Age", city: "City", car: "Has a car",
  availability: "Availability", hobbies: "Hobbies",
};

export const getAboutMe = () => {
  const p = getUserProfile();
  return (p && p.about_me && typeof p.about_me === "object" && !Array.isArray(p.about_me)) ? p.about_me : {};
};

export const setAboutMeFact = (key, value) => {
  if (!key) return getAboutMe();
  const v = (value == null ? "" : String(value)).trim();
  const cur = { ...getAboutMe() };
  if (v) cur[key] = v; else delete cur[key];  // blank clears, never stores empty
  saveUserProfile({ ...getUserProfile(), about_me: cur });
  return cur;
};

export const clearAboutMeFact = (key) => setAboutMeFact(key, "");

export const clearAboutMe = () => {
  const prof = { ...getUserProfile() };
  delete prof.about_me;
  saveUserProfile(prof);
  return {};
};

// --- Clarification telemetry (Phase 2, content-free) -------------------------
// Records shown/answered/dismissed events (+ the chosen ENUM value, never free
// text) so we can measure how often the gate fires and is answered.

export const getClarificationEvents = () => {
  if (_clarifyCache !== null) return _clarifyCache;
  _clarifyCache =
    _readLocalArray(STORAGE_KEYS.clarificationEvents) ||
    _readLocalArray(STORAGE_KEYS.clarificationEventsBackup) || [];
  return _clarifyCache;
};

export const saveClarificationEvents = (events) => {
  const arr = _isArray(events) ? events : [];
  _clarifyCache = arr;
  let serialized;
  try { serialized = JSON.stringify(arr); } catch (e) { return false; }
  let localOk = true;
  try { localStorage.setItem(STORAGE_KEYS.clarificationEvents, serialized); } catch (e) { localOk = false; }
  try { localStorage.setItem(STORAGE_KEYS.clarificationEventsBackup, serialized); } catch (e) { /* ignore */ }
  idbSet(STORAGE_KEYS.clarificationEvents, arr).catch(() => {});
  return localOk || isIdbAvailable();
};

export const recordClarificationEvent = (event) => {
  if (!event || typeof event !== "object") return { ok: true, events: getClarificationEvents() };
  const row = {
    id: `cl_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
    field: event.field || null,
    action: event.action || null,
    value: event.value || null,
    tagged_at: new Date().toISOString().slice(0, 10),
  };
  const next = [...getClarificationEvents(), row];
  const ok = saveClarificationEvents(next);
  return { ok, events: next };
};

export const getClarificationStats = () => {
  const events = getClarificationEvents();
  const counts = { shown: 0, answered: 0, dismissed: 0 };
  events.forEach((e) => { if (e && e.action && counts[e.action] !== undefined) counts[e.action] += 1; });
  return {
    total: events.length,
    shown: counts.shown,
    answered: counts.answered,
    dismissed: counts.dismissed,
    answer_rate: counts.shown ? Math.round((counts.answered / counts.shown) * 100) : 0,
    generatedAt: new Date().toISOString(),
  };
};

// Build the anonymous, id-detached outcome record from a tagged session.
// EXPLICIT field whitelist (not a spread) so nothing identifying can ever leak:
// no session.id, no contactName, no title, no messages, no results, no text.
export const buildAnonymousOutcome = (session) => {
  const o = session && session.outcome;
  if (!o) return null;
  return {
    anon_id: `outcome_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
    schema: o.schema,
    outcome: o.outcome,
    direction: o.direction,
    tone: o.tone,
    signal_level: o.signal_level,
    closing_readiness: o.closing_readiness,
    destination_stage: o.destination_stage,
    depth_bucket: o.depth_bucket,
    exchange_bucket: o.exchange_bucket,
    days_to_outcome: o.days_to_outcome,
    highest_relationship_stage_reached: o.highest_relationship_stage_reached,
    final_relationship_stage: o.final_relationship_stage,
    turns_since_last_advancement: o.turns_since_last_advancement,
    relationship_progression_state: o.relationship_progression_state,
    tagged_at: o.tagged_at,
    archived_at: new Date().toISOString().slice(0, 10),
  };
};

// Preserve a tagged session's anonymous outcome before the session is deleted.
// Returns { outcomes, ok, anonId } (anonId=null if the session was untagged).
export const archiveSessionOutcome = (session) => {
  const record = buildAnonymousOutcome(session);
  if (!record) return { outcomes: getOutcomes(), ok: true, anonId: null };
  const next = [record, ...getOutcomes()];
  const ok = saveOutcomes(next);
  return { outcomes: next, ok, anonId: record.anon_id };
};

// Remove a preserved record (used to keep Undo idempotent so a restored
// conversation does not leave behind a duplicate archived stat).
export const removeOutcome = (anonId) => {
  if (!anonId) return { outcomes: getOutcomes(), ok: true };
  const next = getOutcomes().filter((o) => o.anon_id !== anonId);
  const ok = saveOutcomes(next);
  return { outcomes: next, ok };
};

export const formatTime = (timestamp) => {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

export const getStrategyFromStyle = (style) => {
  const map = { "Smooth": "curiosity", "Playful": "tease", "Bold": "frame", "Minimal": "acceptance" };
  return map[style] || style?.toLowerCase() || "unknown";
};

// Auto stage detection from conversation data
export const detectStage = (history, lastUpdatedAt) => {
  if (!history || history.trim().length === 0) {
    return { stage: "first_time", confidence: "high" };
  }

  // Time-gap logic for sessions with history
  if (lastUpdatedAt) {
    const hoursSince = (Date.now() - lastUpdatedAt) / 3600000;
    if (hoursSince >= 48) return { stage: "restarting", confidence: "high" };
    if (hoursSince >= 12) return { stage: "soft_reset", confidence: "high" };
  }

  const lines = history.split("\n").filter(l => l.trim());
  const hasUserMessages = lines.some(l => /^(you sent|you:|me:)/i.test(l.trim()));
  const hasTheirReplies = lines.some(l => /^(their reply|them:|her:|him:)/i.test(l.trim()));

  if (hasUserMessages && hasTheirReplies) return { stage: "already_talking", confidence: "high" };
  if (lines.length >= 2) return { stage: "already_talking", confidence: "moderate" };
  if (lines.length === 1) return { stage: "first_time", confidence: "moderate" };

  return { stage: "not_sure", confidence: "low" };
};
